import { Logger } from 'src/utils/logger';
import ENV from 'src/env.json';
import { hubspotEvents } from './hubspot.types';

const HUBSPOT_HUB_ID = ENV.HUBSPOT_HUB_ID || '';
const MAX_RETRIES = 5;
const RETRY_DELAY = 500;

declare global {
  interface Window {
    _hsq?: any[];
    HubSpotConversations: {
      widget: {
        open: (options?: { widgetOpen: boolean }) => void;
        close: () => void;
        load: () => void;
        remove: () => void;
        status: (options?: { loaded: boolean }) => void;
        refresh: (options?: { openToNewThread: boolean }) => void;
      };
    };
  }
}

export const isLoaded = () =>
  window?._hsq && typeof window._hsq === 'object' && 'push' in window._hsq;

let loadPromise: Promise<typeof window._hsq> | null = null;

export const load = async () => {
  if (isLoaded()) {
    return window._hsq;
  }

  if (!HUBSPOT_HUB_ID) {
    return undefined;
  }

  if (loadPromise) {
    return loadPromise;
  }

  // Before loading hs, fake existence of form collection script to disable non-HubSpot forms
  // https://community.hubspot.com/t5/Blog-Website-Page-Publishing/Prevent-Cookie-tracking-of-a-specific-form/m-p/527851
  const fakeFormCollectionScript = document.createElement('script');
  fakeFormCollectionScript.id = `CollectedForms-${HUBSPOT_HUB_ID}`;
  fakeFormCollectionScript.type = 'text/javascript';
  document.head.append(fakeFormCollectionScript);

  const script = document.createElement('script');

  script.id = 'hs-script-loader';
  script.type = 'text/javascript';
  script.defer = true;
  script.src = `https://js-eu1.hs-scripts.com/${HUBSPOT_HUB_ID}.js`;

  loadPromise = new Promise<typeof window._hsq>((resolve, reject) => {
    script.onload = () => {
      resolve(window._hsq);
    };

    script.onerror = (err) => {
      loadPromise = null; // Reset on error so it can be retried
      reject(err);
    };

    document.head.append(script);
  });

  return loadPromise;
};

export const push = (args: any[]) => {
  window._hsq?.push(...args);
};

export const identify = async (email: string): Promise<boolean> => {
  // Wait for HubSpot to load if not ready yet
  if (!window._hsq) {
    try {
      await load();
    } catch (error) {
      Logger.error('Failed to load HubSpot before identify', 'HubSpotService', { error });
      return false;
    }
  }

  // After load() resolves, window._hsq should be available
  if (window._hsq) {
    push([hubspotEvents.Identify, { email }]);
    Logger.info('User identified successfully', 'HubSpotService', { email });
    return true;
  }

  Logger.warn('HubSpot loaded but _hsq not available', 'HubSpotService', { email });
  return false;
};

export const trackPageView = () => {
  push([hubspotEvents.TrackPageView]);
};

export const setPathAndTrackPageView = (path: string) => {
  push([hubspotEvents.SetPath, path]);
  push([hubspotEvents.TrackPageView]);
};
